import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowRightLeft, 
  Plus, 
  MapPin,
  Calendar,
  Package,
  MoreVertical,
  ArrowRight
} from 'lucide-react';
import { transfersApi } from '@/api/transfersApi';
import { formatDate } from '@/utils/formatters';
import { RecordTransferModal } from '@/components/transfers/RecordTransferModal';
import { EntityLink } from '@/components/detail/EntityLink';

export function Transfers() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: transfers, isLoading } = useQuery({
    queryKey: ['transfers'],
    queryFn: transfersApi.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: transfersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream">Transfers</h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Track inventory movements between locations
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          Record Transfer
        </button>
      </div>

      {/* Transfers Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>From</th>
              <th>To</th>
              <th>Qty</th>
              <th>Notes</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-600 dark:text-warm-sand">
                  Loading transfers...
                </td>
              </tr>
            ) : transfers?.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-600 dark:text-warm-sand">
                  No transfers recorded yet
                </td>
              </tr>
            ) : (
              transfers?.map((transfer) => (
                <tr key={transfer.id}>
                  <td className="text-gray-600 dark:text-warm-sand">
                    {formatDate(transfer.transferDate)}
                  </td>
                  <td>
                    <div>
                      <EntityLink type="product" id={transfer.productId} label={transfer.productName} />
                      <p className="text-xs text-gray-600 dark:text-warm-sand mono">{transfer.productVariantSku}</p>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 shrink-0" />
                      <EntityLink type="location" id={transfer.fromLocationId} label={transfer.fromLocationName} />
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-amber-450 shrink-0" />
                      <EntityLink type="location" id={transfer.toLocationId} label={transfer.toLocationName} />
                    </div>
                  </td>
                  <td className="font-medium text-gray-900 dark:text-warm-cream">{transfer.quantity}</td>
                  <td className="text-gray-600 dark:text-warm-sand text-sm max-w-xs truncate">
                    {transfer.notes || '-'}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => deleteMutation.mutate(transfer.id)}
                        disabled={deleteMutation.isPending}
                        className="p-2 hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 rounded-lg transition-colors"
                        title="Undo Transfer"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors">
                        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Transfers</p>
              <p className="font-sans text-xl font-bold text-gray-900 dark:text-warm-cream">
                {transfers?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Items Moved</p>
              <p className="font-sans text-xl font-bold text-gray-900 dark:text-warm-cream">
                {transfers?.reduce((sum, t) => sum + t.quantity, 0) || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">This Month</p>
              <p className="font-sans text-xl font-bold text-gray-900 dark:text-warm-cream">
                {transfers?.filter(t => {
                  const date = new Date(t.transferDate);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Transfer Modal */}
      <RecordTransferModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          // Show success toast or notification
        }}
      />
    </div>
  );
}
